import { useEffect, useMemo, useState } from "react"
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom"
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
} from "@mui/material"
import {
    AccountBalance,
    ArrowBack,
    CheckCircle,
    Close,
    Verified,
} from "@mui/icons-material"

import EmptyState from "../../../components/EmptyState"
import NavbarOffset from "../../../components/NavbarOffset"
import VehicleImageSlideshow from "../../../components/VehicleImageSlideshow"
import { PHONE_DISPLAY, PHONE_HREF } from "../../../constants/contactInfo"
import { useCarDetail, useFleetCount, useSimilarCars } from "../../../hooks/useCars"
import { availabilityChipSx, getVehicleFuel, getVehicleTransmission } from "../../../utils/vehicleHelpers"
import { buildHireChecks } from "../constants/hireChecks"
import HirePanel from "./HirePanel"
import OfficeCard from "./OfficeCard"
import SimilarVehicleCard from "./SimilarVehicleCard"
import SpecTile from "./SpecTile"

const VehicleDetailPage = () => {
    const { vehicleId } = useParams()
    const navigate = useNavigate()
    const { vehicle, loading, error } = useCarDetail(vehicleId)
    const { cars: similarVehicles } = useSimilarCars(vehicleId)
    const { count: fleetCount } = useFleetCount()
    const [tab, setTab] = useState(0)
    const [descriptionOpen, setDescriptionOpen] = useState(false)

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" })
    }, [vehicleId])

    const headlineMeta = useMemo(() => {
        if (!vehicle) return []
        return [
            getVehicleFuel(vehicle),
            getVehicleTransmission(vehicle),
            vehicle.specs?.find((s) => s.label === "Seats")?.value,
        ].filter(Boolean)
    }, [vehicle])

    const keySpecTiles = useMemo(() => {
        if (!vehicle?.specs) return []
        return vehicle.specs
            .filter((spec) => !["Make & model", "VIN", "VRM", "Availability"].includes(spec.label))
            .map((spec) => ({ label: spec.label, value: spec.value }))
            .filter((tile) => tile.value && tile.value !== "—")
    }, [vehicle])

    const allFeatures = useMemo(() => {
        if (!vehicle?.features) return []
        return Object.values(vehicle.features).flat()
    }, [vehicle])

    if (loading) {
        return (
            <>
                <NavbarOffset />
                <Box sx={{ py: 10, textAlign: "center", bgcolor: "#f4f7fb" }}>
                    <Typography color="text.secondary">Loading vehicle…</Typography>
                </Box>
            </>
        )
    }

    if (error || !vehicle) {
        return (
            <>
                <NavbarOffset />
                <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: "#f4f7fb" }}>
                    <Container maxWidth="md">
                        <EmptyState
                            title="Vehicle not found"
                            description="This vehicle may no longer be listed. Browse our current fleet or get in touch and we'll help you find a suitable hire car."
                            actions={[
                                { label: "Browse vehicles", href: "/vehicles" },
                                { label: `Call ${PHONE_DISPLAY}`, href: PHONE_HREF, variant: "outlined" },
                            ]}
                        />
                    </Container>
                </Box>
            </>
        )
    }

    const hireChecks = buildHireChecks(vehicle)

    return (
        <Box sx={{ bgcolor: "#f4f7fb", pb: { xs: 6, md: 8 } }}>
            <NavbarOffset />

            <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 3 } }}>
                <Button
                    component={RouterLink}
                    to="/vehicles"
                    startIcon={<ArrowBack />}
                    sx={{ mb: 2, textTransform: "none", fontWeight: 700, color: "#000435" }}
                >
                    Back
                </Button>

                <Typography variant="h4" fontWeight={800} color="#000435" lineHeight={1.15} sx={{ fontSize: { xs: "1.65rem", md: "2rem" } }}>
                    {vehicle.name}
                </Typography>

                {headlineMeta.length > 0 && (
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75, mb: 1.5 }}>
                        {headlineMeta.join(" · ")}
                    </Typography>
                )}

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                    <Chip
                        size="small"
                        label={vehicle.availabilityLabel}
                        sx={{
                            fontWeight: 700,
                            ...availabilityChipSx(vehicle.available),
                        }}
                    />
                    {vehicle.licensingAuthority && (
                        <Chip
                            size="small"
                            icon={<AccountBalance sx={{ fontSize: "18px !important" }} />}
                            label={vehicle.licensingAuthority}
                            sx={{
                                fontWeight: 700,
                                bgcolor: "rgba(0, 163, 255, 0.12)",
                                color: "#000435",
                                border: "1px solid rgba(0, 163, 255, 0.22)",
                            }}
                        />
                    )}
                    {vehicle.tags.map((tag) => (
                        <Chip
                            key={tag}
                            size="small"
                            label={tag}
                            sx={{ fontWeight: 600, bgcolor: "#fff", border: "1px solid rgba(0, 4, 53, 0.1)" }}
                        />
                    ))}
                </Stack>

                <Box sx={{ display: { xs: "block", lg: "none" }, mb: 3 }}>
                    <HirePanel vehicle={vehicle} compact />
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 360px" },
                        gap: { xs: 3, lg: 4 },
                        alignItems: "start",
                        width: "100%",
                        minWidth: 0,
                    }}
                >
                    <Stack spacing={3} sx={{ minWidth: 0, maxWidth: "100%" }}>
                        <VehicleImageSlideshow images={vehicle.images} alt={vehicle.name} />

                        <Box>
                            <Typography variant="h6" fontWeight={800} color="#000435" gutterBottom>
                                Key information
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
                                    gap: 1.5,
                                }}
                            >
                                {keySpecTiles.map((tile) => (
                                    <SpecTile key={tile.label} {...tile} />
                                ))}
                            </Box>
                        </Box>

                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", bgcolor: "#fff" }}>
                            <Tabs
                                value={tab}
                                onChange={(_, value) => setTab(value)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    borderBottom: 1,
                                    borderColor: "divider",
                                    px: 1,
                                    "& .MuiTab-root": { textTransform: "none", fontWeight: 700, minHeight: 52 },
                                }}
                            >
                                <Tab label="Features" />
                                <Tab label="Specifications" />
                                <Tab label="Hire package" />
                                <Tab label="Platforms" />
                            </Tabs>

                            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                                {tab === 0 && (
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                            gap: 1,
                                        }}
                                    >
                                        {allFeatures.map((feature) => (
                                            <Stack key={feature} direction="row" spacing={1} alignItems="flex-start">
                                                <CheckCircle sx={{ fontSize: 17, color: "#00B67A", mt: 0.2 }} />
                                                <Typography variant="body2" color="text.secondary">{feature}</Typography>
                                            </Stack>
                                        ))}
                                    </Box>
                                )}

                                {tab === 1 && (
                                    <Stack spacing={0}>
                                        {vehicle.specs.map((spec, index) => (
                                            <Stack
                                                key={spec.label}
                                                direction="row"
                                                justifyContent="space-between"
                                                spacing={2}
                                                sx={{
                                                    py: 1.25,
                                                    borderBottom: index < vehicle.specs.length - 1 ? "1px solid rgba(0,4,53,0.06)" : undefined,
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary">{spec.label}</Typography>
                                                <Typography variant="body2" fontWeight={600} textAlign="right">{spec.value}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                )}

                                {tab === 2 && (
                                    <Stack spacing={2}>
                                        <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                                            {vehicle.summary}
                                        </Typography>
                                        <Stack spacing={1}>
                                            {vehicle.includes.map((item) => (
                                                <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
                                                    <CheckCircle sx={{ fontSize: 17, color: "#00B67A", mt: 0.15 }} />
                                                    <Typography variant="body2" color="text.secondary">{item}</Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                        <Divider />
                                        <Stack spacing={1}>
                                            {vehicle.hirePackages?.withInsurance && (
                                                <>
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        spacing={2}
                                                    >
                                                        <Typography variant="body2" color="text.secondary">
                                                            Weekly with insurance
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={600} textAlign="right">
                                                            £{vehicle.hirePackages.withInsurance.weekly} per week
                                                        </Typography>
                                                    </Stack>
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        spacing={2}
                                                    >
                                                        <Typography variant="body2" color="text.secondary">
                                                            Deposit with insurance
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={600} textAlign="right">
                                                            £{vehicle.hirePackages.withInsurance.deposit}
                                                        </Typography>
                                                    </Stack>
                                                </>
                                            )}
                                            {vehicle.hirePackages?.withoutInsurance && (
                                                <>
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        spacing={2}
                                                    >
                                                        <Typography variant="body2" color="text.secondary">
                                                            Weekly without insurance
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={600} textAlign="right">
                                                            £{vehicle.hirePackages.withoutInsurance.weekly} per week
                                                        </Typography>
                                                    </Stack>
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        spacing={2}
                                                    >
                                                        <Typography variant="body2" color="text.secondary">
                                                            Deposit without insurance
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={600} textAlign="right">
                                                            £{vehicle.hirePackages.withoutInsurance.deposit}
                                                        </Typography>
                                                    </Stack>
                                                </>
                                            )}
                                            {[
                                                [
                                                    "Minimum term",
                                                    `${vehicle.hire.minWeeks} week${vehicle.hire.minWeeks === 1 ? "" : "s"}`,
                                                ],
                                                ["Mileage", vehicle.hire.mileageNote],
                                                ["Collection", vehicle.hire.collection],
                                            ].map(([label, value]) => (
                                                <Stack
                                                    key={label}
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    spacing={2}
                                                >
                                                    <Typography variant="body2" color="text.secondary">
                                                        {label}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                        textAlign="right"
                                                    >
                                                        {value}
                                                    </Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Stack>
                                )}

                                {tab === 3 && (
                                    <Stack spacing={1.5}>
                                        <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                                            This vehicle is suitable for the following ride-hail and Private Hire platforms.
                                            Platform eligibility can vary — confirm with our team when you enquire.
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {vehicle.platforms.map((platform) => (
                                                <Chip
                                                    key={platform}
                                                    label={platform}
                                                    icon={<Verified sx={{ fontSize: "18px !important" }} />}
                                                    sx={{ fontWeight: 600, bgcolor: "rgba(0, 163, 255, 0.1)", color: "#000435" }}
                                                />
                                            ))}
                                        </Stack>
                                    </Stack>
                                )}
                            </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, bgcolor: "#fff" }}>
                            <Typography variant="h6" fontWeight={800} color="#000435" gutterBottom>
                                Hire eligibility
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                What you need and what&apos;s covered with your hire agreement.
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                    gap: 1.25,
                                }}
                            >
                                {hireChecks.map((check) => (
                                    <Stack key={check.key} direction="row" spacing={1} alignItems="flex-start">
                                        {check.passed === null ? (
                                            <Close sx={{ fontSize: 18, color: "text.secondary", mt: 0.15 }} />
                                        ) : check.passed ? (
                                            <CheckCircle sx={{ fontSize: 18, color: "#00B67A", mt: 0.15 }} />
                                        ) : (
                                            <Close sx={{ fontSize: 18, color: "warning.main", mt: 0.15 }} />
                                        )}
                                        <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                                            {check.label}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, bgcolor: "#fff" }}>
                            <Typography variant="h6" fontWeight={800} color="#000435" gutterBottom>
                                Description
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                lineHeight={1.8}
                                sx={
                                    descriptionOpen
                                        ? undefined
                                        : {
                                              display: "-webkit-box",
                                              WebkitLineClamp: 4,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                          }
                                }
                            >
                                {vehicle.description}
                            </Typography>
                            {vehicle.description.length > 220 && (
                                <Button
                                    size="small"
                                    onClick={() => setDescriptionOpen((open) => !open)}
                                    sx={{ mt: 1, textTransform: "none", fontWeight: 700, px: 0 }}
                                >
                                    {descriptionOpen ? "Read less" : "Read more"}
                                </Button>
                            )}
                        </Paper>

                        {similarVehicles.length > 0 && (
                            <Box>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                    <Typography variant="h6" fontWeight={800} color="#000435">
                                        Similar vehicles
                                    </Typography>
                                    <Button component={RouterLink} to="/vehicles" size="small" sx={{ textTransform: "none", fontWeight: 700 }}>
                                        View all
                                    </Button>
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    sx={{
                                        overflowX: "auto",
                                        overflowY: "hidden",
                                        pb: 1,
                                        width: "100%",
                                        maxWidth: "100%",
                                        minWidth: 0,
                                        WebkitOverflowScrolling: "touch",
                                    }}
                                >
                                    {similarVehicles.map((car) => (
                                        <SimilarVehicleCard
                                            key={car._id}
                                            car={car}
                                            onClick={() => navigate(`/vehicles/${car._id}`)}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Stack>

                    <Box
                        sx={{
                            display: { xs: "none", lg: "block" },
                            position: "sticky",
                            top: 88,
                            alignSelf: "start",
                        }}
                    >
                        <Stack spacing={2}>
                            <HirePanel vehicle={vehicle} />
                            <OfficeCard fleetCount={fleetCount} />
                        </Stack>
                    </Box>
                </Box>

                <Box sx={{ display: { xs: "block", lg: "none" }, mt: 3 }}>
                    <OfficeCard fleetCount={fleetCount} />
                </Box>
            </Container>
        </Box>
    )
}

export default VehicleDetailPage
